import { cn } from "./classNames-BzrkDnOD.js";
import { mergeRefs } from "./internal/utils/mergeRefs.js";
import { createContext, use, useEffect, useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/blocks/SectionNav/constants.ts
/**
* Names the container query `SectionNav` reads to pick the tree over the dropdown.
*
* It has to sit on an **ancestor**: a container query matches descendants only, so the element that
* names the container can never read it — and it cannot be `SectionNav`'s own wrapper either, whose
* width is what the switch changes (256px in a row, full width in a column), which would oscillate.
* `PageLayout.Content nav={…}` puts it on the right element for you.
*/
const SECTION_NAV_CONTAINER = "tw:@container/section-nav";
//#endregion
//#region src/blocks/PageLayout/context.ts
/**
* The `PageLayout` root element, so `PageLayout.Header` can publish its measured height as a CSS
* custom property the whole page can read — `PageLayout.Content nav={…}` pins the nav to it.
*
* A slot rendered outside a `PageLayout` reads `null` and skips publishing rather than throwing:
* `PageLayout.Footer` is already documented as usable on its own.
*/
const PageLayoutRootContext = createContext(null);
const usePageLayoutRoot = () => use(PageLayoutRootContext);
//#endregion
//#region src/blocks/PageLayout/PageLayout.tsx
const maxWidthClass = {
	narrow: "tw:max-w-[976px]",
	default: "tw:max-w-[1280px]",
	wide: "tw:max-w-[1600px]",
	full: "tw:max-w-full"
};
const Root = ({ ref, className, maxWidth = "full", children, ...props }) => {
	const rootRef = useRef(null);
	return /* @__PURE__ */ jsx(PageLayoutRootContext, {
		value: rootRef,
		children: /* @__PURE__ */ jsx("div", {
			ref: mergeRefs(ref ?? null, rootRef),
			className: cn("tw:flex tw:flex-col tw:gap-4 tw:w-full tw:mx-auto", maxWidthClass[maxWidth], className),
			...props,
			children
		})
	});
};
const Breadcrumbs = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:w-full", className),
	...props,
	children
});
const STICKY_CHROME_BLEED = "tw:before:absolute tw:before:inset-x-[-0.5rem] tw:before:inset-y-0 tw:before:-z-10 tw:before:bg-background";
const Header = ({ ref, className, sticky = false, children, ...props }) => {
	const rootRef = usePageLayoutRoot();
	const headerRef = useRef(null);
	useEffect(() => {
		const header = headerRef.current;
		const root = rootRef?.current;
		if (!sticky || !header || !root || typeof ResizeObserver === "undefined") return;
		const publish = () => {
			root.style.setProperty("--page-header-height", `${Math.round(header.getBoundingClientRect().height)}px`);
		};
		const observer = new ResizeObserver(publish);
		observer.observe(header);
		publish();
		return () => {
			observer.disconnect();
			root.style.removeProperty("--page-header-height");
		};
	}, [sticky, rootRef]);
	return /* @__PURE__ */ jsxs("header", {
		ref: mergeRefs(ref ?? null, headerRef),
		className: cn("tw:w-full", sticky && ["tw:sticky tw:top-0 tw:z-20 tw:bg-background", STICKY_CHROME_BLEED], className),
		...props,
		children: [children, sticky && /* @__PURE__ */ jsx("div", {
			"aria-hidden": true,
			className: "tw:pointer-events-none tw:absolute tw:inset-x-0 tw:top-full tw:h-6 tw:bg-linear-to-b tw:from-background tw:to-[rgb(var(--background)/0)]"
		})]
	});
};
const Tabs = ({ ref, className, children, ...props }) => /* @__PURE__ */ jsx("div", {
	ref,
	className: cn("tw:flex tw:w-full tw:items-center tw:border-b tw:border-border", className),
	...props,
	children
});
const scrollParent = (from) => {
	for (let el = from.parentElement; el; el = el.parentElement) if (/auto|scroll/.test(getComputedStyle(el).overflowY)) return el;
	return null;
};
const useAvailableHeight = () => {
	const rowRef = useRef(null);
	useEffect(() => {
		const row = rowRef.current;
		if (!row || typeof ResizeObserver === "undefined") return;
		const scroller = scrollParent(row);
		const publish = () => {
			const top = row.getBoundingClientRect().top + (scroller?.scrollTop ?? window.scrollY);
			const room = scroller ? scroller.clientHeight - (top - scroller.getBoundingClientRect().top) : window.innerHeight - top;
			row.style.setProperty("--section-nav-available", `${Math.max(0, Math.round(room))}px`);
		};
		const observer = new ResizeObserver(publish);
		observer.observe(row);
		if (scroller) observer.observe(scroller);
		publish();
		return () => observer.disconnect();
	}, []);
	return rowRef;
};
const Content = ({ ref, className, nav, children, ...props }) => {
	const rowRef = useAvailableHeight();
	if (!nav) return /* @__PURE__ */ jsx("div", {
		ref,
		className: cn("tw:flex tw:w-full tw:flex-1 tw:flex-col", className),
		...props,
		children
	});
	return /* @__PURE__ */ jsx("div", {
		ref,
		className: cn("tw:group/page-layout-content tw:flex tw:w-full tw:flex-1 tw:flex-col", SECTION_NAV_CONTAINER, className),
		...props,
		children: /* @__PURE__ */ jsxs("div", {
			ref: rowRef,
			className: "tw:flex tw:w-full tw:@[640px]/section-nav:flex-row tw:@[640px]/section-nav:gap-8 tw:@max-[640px]/section-nav:flex-col tw:@max-[640px]/section-nav:gap-4",
			children: [/* @__PURE__ */ jsx("div", {
				"data-slot": "page-layout-nav",
				className: "tw:sticky tw:top-[calc(var(--page-header-height,0px)+1rem)] tw:z-10 tw:shrink-0 tw:self-start tw:@[640px]/section-nav:max-h-[var(--section-nav-available,100dvh)] tw:@[640px]/section-nav:w-64 tw:@[640px]/section-nav:overflow-y-auto tw:@[640px]/section-nav:supports-[animation-timeline:scroll()]:scroll-fade-y tw:@max-[640px]/section-nav:w-full tw:@max-[640px]/section-nav:bg-background tw:@max-[640px]/section-nav:before:absolute tw:@max-[640px]/section-nav:before:inset-x-[-0.5rem] tw:@max-[640px]/section-nav:before:top-[-1rem] tw:@max-[640px]/section-nav:before:bottom-0 tw:@max-[640px]/section-nav:before:-z-10 tw:@max-[640px]/section-nav:before:bg-background tw:@max-[640px]/section-nav:after:pointer-events-none tw:@max-[640px]/section-nav:after:absolute tw:@max-[640px]/section-nav:after:inset-x-0 tw:@max-[640px]/section-nav:after:top-full tw:@max-[640px]/section-nav:after:h-6 tw:@max-[640px]/section-nav:after:bg-linear-to-b tw:@max-[640px]/section-nav:after:from-background tw:@max-[640px]/section-nav:after:to-[rgb(var(--background)/0)]",
				children: nav
			}), /* @__PURE__ */ jsx("div", {
				"data-slot": "page-layout-content-body",
				className: "tw:[--page-content-sticky-top:var(--page-header-height,0px)] tw:min-w-0 tw:flex-1",
				children
			})]
		})
	});
};
const Footer = ({ ref, className, sticky = false, children, ...props }) => /* @__PURE__ */ jsx("footer", {
	ref,
	className: cn("tw:flex tw:w-full tw:items-center tw:justify-end tw:gap-3 tw:border-t tw:border-border tw:bg-background tw:px-6 tw:py-4", sticky && ["tw:sticky tw:bottom-0 tw:z-10", STICKY_CHROME_BLEED], className),
	...props,
	children
});
/**
* Page chrome for every new kbc-ui page: max-width column + vertical rhythm + named slots.
*
* - No outer padding: the shell owns the gutter to the app frame.
* - Not for modal bodies or embedded widgets; the `Sidebar` lives in the app shell.
* - Search / filter pills go in `PageToolbar`, never in the header.
*
* @see RECIPES.md#list-detail
* @see RECIPES.md#section-nav
*/
const PageLayout = Object.assign(Root, {
	/** Usually composed by `PageHeader.Detail`; for layouts that fit neither `PageHeader` variant. */
	Breadcrumbs,
	/** `sticky` also publishes its height as `--page-header-height`, which `Content nav` pins to. */
	Header,
	/** Routing-based tab rows; use `Tabs` / `TabsContent` inside, never a raw `<button>`. */
	Tabs,
	/** `nav` (a `SectionNav`) makes it a two-column layout. */
	Content,
	/** Form submit bar; `sticky` pins it to the viewport. */
	Footer
});
//#endregion
export { PageLayout };

//# sourceMappingURL=PageLayout-DiAAILuS.js.map